import { useEffect } from 'react';
import { useMatch } from '@tanstack/react-location';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { startCase } from 'lodash-es';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { BulkReviewInvitationsComponents } from '@frontend/bulk-review-invitations';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { Button, Heading, useModalControl } from '@frontend/design-system';
import { useReviewsFilterShallowStore } from '../../hooks';
import { ReviewFilter } from '../review-filter';

export const ReviewHeader = () => {
  const { t } = useTranslation('reviews');
  const { sourceFilter, setSourceFilter } = useReviewsFilterShallowStore('sourceFilter', 'setSourceFilter');
  const { params } = useMatch();
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const aclEnforcementEnabled = getFeatureFlagValue('nwx:messaging-inbox-fe-acl-enforcement', 'ALL');
  const integratedMessagingACLEnforcementEnabled = getFeatureFlagValue(
    'nwx:messaging-integrated-messaging-fe-acl-enforcement',
    'ALL'
  );
  const locationIdsWithInboxACL = integratedMessagingACLEnforcementEnabled
    ? filterLocationIdsByACLs({
        locationIds: selectedLocationIds,
        acls: Permission.SMS_INBOX_MANAGE,
      })
    : selectedLocationIds;
  const locationIdsWithACL = aclEnforcementEnabled
    ? filterLocationIdsByACLs({
        locationIds: selectedLocationIds,
        acls: Permission.REVIEW_READ,
      })
    : selectedLocationIds;

  useEffect(() => {
    setSourceFilter(params.site as typeof sourceFilter);
  }, [params.site]);

  const modalControls = useModalControl();

  return (
    <>
      <section
        css={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Heading>{startCase(sourceFilter?.replace(/-/gi, ' '))}</Heading>
        <div
          css={{
            display: 'flex',
          }}
        >
          <ReviewFilter disabled={!locationIdsWithACL.length} />
          <Button
            size='large'
            variant='secondary'
            iconName='send-review-request'
            {...modalControls.triggerProps}
            disabled={!locationIdsWithInboxACL.length && !!selectedLocationIds.length}
            hoverLabel={
              !locationIdsWithInboxACL.length && !!selectedLocationIds.length
                ? t('You do not have permission to send text messages')
                : t('Request Reviews')
            }
            showLabelWhenDisabled={!locationIdsWithInboxACL.length && !!selectedLocationIds.length}
          />
        </div>
      </section>
      <BulkReviewInvitationsComponents.BulkReviewInvitationsModal {...modalControls.modalProps} />
    </>
  );
};
